import React, { useState } from 'react';
import {
  Container, Row, Col, Card, Badge, Button, Spinner, Alert,
  Form, InputGroup, Modal, Dropdown
} from 'react-bootstrap';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import {
  listActiveAssignments,
  returnTool,
  reportToolLost,
  getToolAssignmentHistory,
  assignTool,
  listTools
} from '../../services/toolAssignmentService';
import { getUsersByRole } from '../../services/userService';
import { listProjects } from '../../services/projectService';
import { PROJECT_STATUS } from '../../utils/constants';
import DashboardLayout from '../../components/layout/DashboardLayout';
import useViewToggle from '../../hooks/useViewToggle';
import ViewToggle from '../../components/common/ViewToggle';
import KebabMenu from '../../components/common/KebabMenu';

const daysBetween = (iso) => {
  if (!iso) return 0;
  return Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));
};

const ToolAssignments = () => {
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const { viewMode, setViewMode, isCardView } = useViewToggle();
  const [projectFilter, setProjectFilter] = useState('');
  const [searchHolder, setSearchHolder] = useState('');
  const [historyModal, setHistoryModal] = useState(null);
  const [actionModal, setActionModal] = useState(null);
  const [actionNotes, setActionNotes] = useState('');
  const [actionError, setActionError] = useState('');
  const [assignModal, setAssignModal] = useState(false);
  const [adminTechId, setAdminTechId] = useState('');
  const [adminToolId, setAdminToolId] = useState('');
  const [adminProjectId, setAdminProjectId] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [adminError, setAdminError] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['active-assignments'],
    queryFn: () => listActiveAssignments({ limit: 200 }),
    enabled: isAdmin,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true
  });

  const { data: historyData, isLoading: loadingHistory } = useQuery({
    queryKey: ['assignment-history', historyModal?.tool_id],
    queryFn: () => getToolAssignmentHistory(historyModal.tool_id, { limit: 50 }),
    enabled: !!historyModal?.tool_id
  });

  const { data: techniciansData } = useQuery({
    queryKey: ['technicians-list'],
    queryFn: () => getUsersByRole('technician'),
    enabled: assignModal,
    staleTime: 5 * 60 * 1000
  });

  const { data: toolsData } = useQuery({
    queryKey: ['tools-available-admin'],
    queryFn: () => listTools({ status: 'available', limit: 200 }),
    enabled: assignModal,
    staleTime: 60 * 1000
  });

  const { data: projectsData } = useQuery({
    queryKey: ['projects-list'],
    // Issue #296: 'active' no longer exists in the live status set; the
    // in-execution status is now 'in_progress'.
    queryFn: () => listProjects({ status: PROJECT_STATUS.IN_PROGRESS, pageSize: 100 }),
    enabled: assignModal,
    staleTime: 5 * 60 * 1000
  });

  const adminAssignMutation = useMutation({
    mutationFn: () => assignTool({
      tool_id: adminToolId,
      holder_id: adminTechId,
      project_id: adminProjectId || null,
      notes: adminNotes || null
    }),
    onSuccess: ({ error: err }) => {
      if (err) { setAdminError(err.message); return; }
      queryClient.invalidateQueries({ queryKey: ['active-assignments'] });
      queryClient.invalidateQueries({ queryKey: ['tools-available-admin'] });
      queryClient.invalidateQueries({ queryKey: ['tools-available'] });
      setAssignModal(false);
    },
    onError: (e) => setAdminError(e.message)
  });

  const actionMutation = useMutation({
    mutationFn: () => actionModal.mode === 'lost'
      // `reportToolLost` también cierra la asignación y avisa a los admins:
      // `updateToolCondition` sólo cambiaba la condición y la fila seguía
      // apareciendo en esta misma pantalla como asignación activa.
      ? reportToolLost({ tool_id: actionModal.tool_id, reason: actionNotes || null })
      : returnTool({ assignment_id: actionModal.id, return_destination: 'warehouse', notes: actionNotes || null }),
    onSuccess: ({ error: err }) => {
      if (err) { setActionError(err.message); return; }
      queryClient.invalidateQueries({ queryKey: ['active-assignments'] });
      queryClient.invalidateQueries({ queryKey: ['tools-available'] });
      queryClient.invalidateQueries({ queryKey: ['tools-available-admin'] });
      setActionModal(null);
    },
    onError: (e) => setActionError(e.message)
  });

  const allAssignments = data?.data || [];

  const filtered = allAssignments.filter(a => {
    const holderName = a.holder?.full_name || '';
    if (searchHolder && !holderName.toLowerCase().includes(searchHolder.toLowerCase())) return false;
    if (projectFilter && a.project_id !== projectFilter) return false;
    return true;
  });

  const uniqueProjects = [...new Map(
    allAssignments
      .filter(a => a.project_id)
      .map(a => [a.project_id, { id: a.project_id, name: a.project?.title || a.project?.project_number || a.project_id }])
  ).values()];

  const openAction = (a, mode) => {
    setActionModal({ id: a.id, tool_id: a.tool?.id, mode, name: a.tool?.name });
    setActionNotes('');
    setActionError('');
  };

  const openHistory = (a) => {
    setHistoryModal({ tool_id: a.tool?.id, name: a.tool?.name });
  };

  const openAssign = () => {
    setAssignModal(true);
    setAdminTechId('');
    setAdminToolId('');
    setAdminProjectId('');
    setAdminNotes('');
    setAdminError('');
  };

  const renderCard = (a) => {
    const daysOut = daysBetween(a.assigned_at);
    return (
      <Col key={a.id} xs={12} sm={6} lg={4} className="mb-3">
        <Card className="h-100 modern-card">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-start">
              <div style={{ flex: 1 }}>
                <Card.Title className="mb-1 fs-6 fw-bold">{a.tool?.name || 'Herramienta'}</Card.Title>
                <small className="text-muted font-monospace">{a.tool?.serial_number}</small>
                {a.tool?.category && <Badge bg="light" text="dark" className="ms-2">{a.tool.category}</Badge>}
              </div>
              <div className="d-flex align-items-center gap-1">
                <Badge bg="primary"><i className="bi bi-person-check me-1" />Activa</Badge>
                <KebabMenu label={`Acciones de ${a.tool?.name || 'Herramienta'}`} stopPropagation={false}>
                    <Dropdown.Item onClick={() => openHistory(a)}>
                      <i className="bi bi-clock-history me-2" />Ver historial
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => openAction(a, 'return')}>
                      <i className="bi bi-box-arrow-in-left me-2 text-success" />Forzar devolución
                    </Dropdown.Item>
                    <Dropdown.Item className="text-danger" onClick={() => openAction(a, 'lost')}>
                      <i className="bi bi-exclamation-triangle me-2" />Marcar perdida
                    </Dropdown.Item>
                </KebabMenu>
              </div>
            </div>

            <div className="mt-2 d-flex align-items-center gap-2">
              <div
                className="hex-avatar hex-avatar-sm d-flex align-items-center justify-content-center text-white fw-bold"
                style={{
                  background: '#0d6efd', fontSize: '0.6rem', width: 28, height: 28,
                  clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)'
                }}
              >
                {(a.holder?.full_name || '?').charAt(0).toUpperCase()}
              </div>
              <span className="small">{a.holder?.full_name || '—'}</span>
            </div>

            {a.project && (
              <div className="mt-1 small text-muted">
                <i className="bi bi-briefcase me-1" />{a.project.title || a.project.project_number}
              </div>
            )}
            <div className="mt-1 small text-muted">
              <i className="bi bi-calendar me-1" />
              {new Date(a.assigned_at).toLocaleDateString('es-CR')}
              {daysOut > 0 && <span className={`ms-1 ${daysOut > 7 ? 'text-warning fw-bold' : ''}`}>({daysOut}d)</span>}
            </div>
          </Card.Body>
        </Card>
      </Col>
    );
  };

  const renderRow = (a) => {
    const daysOut = daysBetween(a.assigned_at);
    return (
      <tr key={a.id}>
        <td>
          <strong>{a.tool?.name || 'Herramienta'}</strong><br />
          <small className="text-muted font-monospace">{a.tool?.serial_number}</small>
        </td>
        <td>
          <div className="d-flex align-items-center gap-2">
            <div
              className="hex-avatar hex-avatar-sm d-flex align-items-center justify-content-center text-white fw-bold flex-shrink-0"
              style={{
                background: '#0d6efd', fontSize: '0.6rem', width: 28, height: 28,
                clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)'
              }}
            >
              {(a.holder?.full_name || '?').charAt(0).toUpperCase()}
            </div>
            {a.holder?.full_name || '—'}
          </div>
        </td>
        <td>{a.project?.title || a.project?.project_number || '—'}</td>
        <td>{new Date(a.assigned_at).toLocaleDateString('es-CR')}</td>
        <td>
          {daysOut > 0
            ? <span className={daysOut > 7 ? 'text-warning fw-bold' : ''}>{daysOut}d</span>
            : '—'}
        </td>
        <td>
          <KebabMenu label={`Acciones de ${a.tool?.name || 'Herramienta'}`} stopPropagation={false}>
              <Dropdown.Item onClick={() => openHistory(a)}>
                <i className="bi bi-clock-history me-2" />Ver historial
              </Dropdown.Item>
              <Dropdown.Item onClick={() => openAction(a, 'return')}>
                <i className="bi bi-box-arrow-in-left me-2 text-success" />Forzar devolución
              </Dropdown.Item>
              <Dropdown.Item className="text-danger" onClick={() => openAction(a, 'lost')}>
                <i className="bi bi-exclamation-triangle me-2" />Marcar perdida
              </Dropdown.Item>
          </KebabMenu>
        </td>
      </tr>
    );
  };

  if (!isAdmin) return <Alert variant="danger" className="m-3">Acceso restringido a administradores.</Alert>;

  const availableTools = toolsData?.data || [];

  return (
    <DashboardLayout>
      <Container fluid className="py-3">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <h2 className="modern-title mb-0">
            <i className="bi bi-tools me-2" />Asignaciones activas
          </h2>
          <div className="d-flex gap-2 align-items-center">
            <Badge bg="primary" className="fs-6">{filtered.length} activas</Badge>
            <Button size="sm" variant="outline-primary" onClick={openAssign}>
              <i className="bi bi-plus-circle me-1" />Asignar
            </Button>
            <ViewToggle viewMode={viewMode} onToggle={setViewMode} />
          </div>
        </div>

        <Row className="g-2 mb-3">
          <Col xs={12} sm={6} md={4}>
            <InputGroup>
              <InputGroup.Text><i className="bi bi-person-search" /></InputGroup.Text>
              <Form.Control
                placeholder="Buscar por técnico..."
                value={searchHolder}
                onChange={e => setSearchHolder(e.target.value)}
              />
            </InputGroup>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Form.Select value={projectFilter} onChange={e => setProjectFilter(e.target.value)}>
              <option value="">Todos los proyectos</option>
              {uniqueProjects.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </Form.Select>
          </Col>
        </Row>

        {isLoading && <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>}
        {error && <Alert variant="danger">Error al cargar asignaciones: {error.message}</Alert>}

        {!isLoading && filtered.length === 0 && (
          <Alert variant="info">No hay asignaciones activas.</Alert>
        )}

        {!isLoading && filtered.length > 0 && (
          isCardView ? (
            <Row>{filtered.map(renderCard)}</Row>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead>
                  <tr>
                    <th>Herramienta</th>
                    <th>Técnico</th>
                    <th>Proyecto</th>
                    <th>Fecha</th>
                    <th>Días</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>{filtered.map(renderRow)}</tbody>
              </table>
            </div>
          )
        )}
      </Container>

      <Modal show={assignModal} onHide={() => setAssignModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title><i className="bi bi-plus-circle me-2" />Asignar herramienta</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {adminError && <Alert variant="danger">{adminError}</Alert>}
          <Form.Group className="mb-3">
            <Form.Label>Técnico <span className="text-danger">*</span></Form.Label>
            <Form.Select value={adminTechId} onChange={e => setAdminTechId(e.target.value)}>
              <option value="">Seleccionar técnico...</option>
              {(techniciansData?.data || techniciansData || []).map(u => (
                <option key={u.id} value={u.id}>{u.full_name}</option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Herramienta <span className="text-danger">*</span></Form.Label>
            <Form.Select value={adminToolId} onChange={e => setAdminToolId(e.target.value)}>
              <option value="">Seleccionar herramienta...</option>
              {availableTools.map(t => (
                <option key={t.id} value={t.id}>
                  {t.name} — {t.serial_number}{t.brand ? ` (${t.brand})` : ''}
                </option>
              ))}
            </Form.Select>
            {availableTools.length === 0 && (
              <Form.Text className="text-muted">No hay herramientas disponibles para asignar.</Form.Text>
            )}
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Proyecto</Form.Label>
            <Form.Select value={adminProjectId} onChange={e => setAdminProjectId(e.target.value)}>
              <option value="">Sin proyecto</option>
              {(projectsData?.data || []).map(p => (
                <option key={p.id} value={p.id}>{p.project_number ? `${p.project_number} — ` : ''}{p.title}</option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label>Notas</Form.Label>
            <Form.Control as="textarea" rows={2} placeholder="Propósito o condición al salir..." value={adminNotes} onChange={e => setAdminNotes(e.target.value)} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setAssignModal(false)}>Cancelar</Button>
          <Button variant="primary" disabled={!adminTechId || !adminToolId || adminAssignMutation.isPending} onClick={() => adminAssignMutation.mutate()}>
            {adminAssignMutation.isPending && <Spinner size="sm" animation="border" className="me-1" />}
            Confirmar asignación
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!historyModal} onHide={() => setHistoryModal(null)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title><i className="bi bi-clock-history me-2" />Historial: {historyModal?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingHistory && <div className="text-center py-3"><Spinner animation="border" /></div>}
          {!loadingHistory && (
            <div className="table-responsive">
              <table className="table table-sm align-middle">
                <thead>
                  <tr><th>Técnico</th><th>Proyecto</th><th>Asignada</th><th>Devuelta</th><th>Notas</th></tr>
                </thead>
                <tbody>
                  {(historyData?.data || []).map(h => (
                    <tr key={h.id}>
                      <td>{h.holder?.full_name || '—'}</td>
                      <td>{h.project?.title || h.project?.project_number || '—'}</td>
                      <td>{new Date(h.assigned_at).toLocaleDateString('es-CR')}</td>
                      <td>{h.returned_at ? new Date(h.returned_at).toLocaleDateString('es-CR') : <span className="text-primary fw-bold">Activa</span>}</td>
                      <td><small className="text-muted">{h.notes || '—'}</small></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!historyData?.data?.length && <p className="text-muted text-center">Sin historial previo.</p>}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setHistoryModal(null)}>Cerrar</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!actionModal} onHide={() => setActionModal(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className={`bi ${actionModal?.mode === 'lost' ? 'bi-exclamation-triangle text-danger' : 'bi-box-arrow-in-left text-success'} me-2`} />
            {actionModal?.mode === 'lost' ? 'Marcar como perdida' : 'Forzar devolución'}: {actionModal?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {actionError && <Alert variant="danger">{actionError}</Alert>}
          {actionModal?.mode === 'lost' && (
            <Alert variant="warning">Esta acción marcará la herramienta como perdida.</Alert>
          )}
          <Form.Group>
            <Form.Label>Notas</Form.Label>
            <Form.Control
              as="textarea" rows={2}
              placeholder="Motivo o detalles..."
              value={actionNotes} onChange={e => setActionNotes(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setActionModal(null)}>Cancelar</Button>
          <Button
            variant={actionModal?.mode === 'lost' ? 'danger' : 'success'}
            disabled={actionMutation.isPending}
            onClick={() => actionMutation.mutate()}
          >
            {actionMutation.isPending && <Spinner size="sm" animation="border" className="me-1" />}
            Confirmar
          </Button>
        </Modal.Footer>
      </Modal>
    </DashboardLayout>
  );
};

export default ToolAssignments;
